import React, { useEffect, useState } from "react";
import { ukDate } from "../lib/dates";
import api, { formatApiError } from "../lib/api";
import { useConfirm } from "../context/ConfirmContext";
import { undoableDelete, logDeletion } from "../lib/undo";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Plus, Users2, Pencil, Trash2, ListTodo, Crown, UserCheck, Check, X, Search, PhoneCall, Lock, LockOpen, UserPlus } from "lucide-react";
import { usePaged, Paginator } from "../components/Paginator";
import { TeamsOverview } from "../components/teams/TeamsOverview";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";
const inputCls = "w-full bg-[#EDF2F4] border border-[#EBE9E4] rounded-xl focus:ring-2 focus:ring-[#0B5E4E]/20 focus:border-[#0B5E4E] outline-none transition-all px-4 py-2.5 text-sm";

const empty = { name: "", description: "", lead_name: "", category: "Ministry", member_ids: [] };

// Quick, invite-only member add for confidential teams (members can't self-apply to these).
function QuickAddMember({ team, members, onAdd }) {
  const [q, setQ] = useState("");
  const query = q.trim().toLowerCase();
  const onTeam = new Set(team.member_ids || []);
  const matches = query
    ? members
        .filter((m) => !onTeam.has(m.id))
        .filter((m) => `${m.first_name} ${m.last_name} ${m.email || ""}`.toLowerCase().includes(query))
        .slice(0, 6)
    : [];
  return (
    <div className="mt-2 rounded-xl px-3 py-2.5 border" style={{ borderColor: "var(--sj-border)", background: "#F7F7F5" }} data-testid={`team-quick-add-${team.id}`}>
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>
        <UserPlus className="h-3.5 w-3.5" /> Invite-only — add a member directly
      </div>
      <div className="relative">
        <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: "var(--sj-primary)" }} />
        <input
          data-testid={`team-quick-add-input-${team.id}`}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search members by name or email…"
          className="w-full bg-white border-2 border-[#0B5E4E] rounded-lg outline-none focus:ring-2 focus:ring-[#0B5E4E]/30 pl-8 pr-3 py-2 text-sm"
        />
      </div>
      {query && (
        <div className="mt-1.5 space-y-1" data-testid={`team-quick-add-results-${team.id}`}>
          {matches.length === 0 ? (
            <p className="text-xs px-1 py-1" style={{ color: "var(--sj-text-3)" }}>No matching members not already on this team.</p>
          ) : (
            matches.map((m) => (
              <button
                key={m.id}
                type="button"
                data-testid={`team-quick-add-option-${team.id}-${m.id}`}
                onClick={() => { onAdd(team.id, m.id, `${m.first_name} ${m.last_name}`.trim()); setQ(""); }}
                className="w-full flex items-center justify-between gap-2 rounded-lg px-2.5 py-1.5 text-sm hover:bg-[#E3F1EC] transition-colors text-left"
              >
                <span className="min-w-0">
                  <span className="font-medium truncate block" style={{ color: "var(--sj-text)" }}>{m.first_name} {m.last_name}</span>
                  {m.email && <span className="text-xs truncate block" style={{ color: "var(--sj-text-3)" }}>{m.email}</span>}
                </span>
                <span className="inline-flex items-center gap-1 text-xs font-semibold shrink-0" style={{ color: "var(--sj-primary)" }}><Plus className="h-3.5 w-3.5" /> Add</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default function Teams() {
  const [teams, setTeams] = useState([]);
  const [teamSearch, setTeamSearch] = useState("");
  const confirm = useConfirm();
  const [members, setMembers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [tasksOpen, setTasksOpen] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [taskForm, setTaskForm] = useState({ title: "", assignee_name: "", priority: "medium", status: "todo", due_date: "" });
  const [applications, setApplications] = useState([]);
  const [assignFor, setAssignFor] = useState(null);
  const [assignForm, setAssignForm] = useState({ name: "", email: "", password: "", member_id: "" });
  const [assignMode, setAssignMode] = useState("member");
  const [memberSearch, setMemberSearch] = useState("");

  const load = async () => {
    const [t, m, a] = await Promise.all([api.get("/teams"), api.get("/members"), api.get("/team-applications?status=pending")]);
    setTeams(t.data); setMembers(m.data); setApplications(a.data);
  };
  useEffect(() => { load(); }, []);

  const decideApp = async (id, action) => {
    try {
      await api.post(`/team-applications/${id}/${action}`);
      toast.success(action === "approve" ? "Application approved — member added to team" : "Application rejected");
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openAssign = (t) => {
    const tm = members.filter((m) => (t.member_ids || []).includes(m.id));
    setAssignFor(t);
    setAssignMode(tm.length ? "member" : "email");
    setAssignForm({ name: "", email: "", password: "", member_id: "" });
  };
  const toggleCalling = async (t) => {
    try {
      await api.put(`/teams/${t.id}/calling`, { can_call: !t.can_call });
      toast.success(!t.can_call ? `${t.name} can now call members` : `Calling disabled for ${t.name}`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggleConfidential = async (t) => {
    try {
      await api.put(`/teams/${t.id}/confidential`, { confidential: !t.confidential });
      toast.success(!t.confidential
        ? `${t.name} is now confidential — members can't see or apply to it`
        : `${t.name} is now open for members to apply`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const addMemberToTeam = async (teamId, memberId, memberName) => {
    try {
      await api.post(`/teams/${teamId}/add-member`, { member_id: memberId });
      toast.success(`${memberName} added to the team`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const toggleAssistantHead = async (t, memberId) => {
    const cur = t.assistant_head_ids || [];
    const next = cur.includes(memberId) ? cur.filter((x) => x !== memberId) : [...cur, memberId];
    try {
      await api.put(`/teams/${t.id}/assistant-heads`, { member_ids: next });
      toast.success("Assistant Heads updated");
      await refreshAssign(t.id);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const refreshAssign = async (teamId) => {
    const { data } = await api.get("/teams");
    setTeams(data);
    const t = data.find((x) => x.id === teamId);
    if (t) setAssignFor(t);
  };
  const saveAssign = async (e) => {
    e.preventDefault();
    try {
      if (assignMode === "member") {
        if (!assignForm.member_id) { toast.error("Please choose a member to make leader"); return; }
        await api.post(`/teams/${assignFor.id}/assign-leader`, { member_id: assignForm.member_id });
      } else {
        await api.post(`/teams/${assignFor.id}/assign-leader`, { name: assignForm.name, email: assignForm.email, password: assignForm.password });
      }
      toast.success("Team leader added");
      setAssignForm({ name: "", email: "", password: "", member_id: "" });
      await refreshAssign(assignFor.id);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const unassignLeader = async (leader) => {
    if (!(await confirm({ title: "Remove team leader?", description: `Remove ${leader.name || "this leader"} from leading this team? They stay a member — they just won't lead it anymore.`, confirmLabel: "Remove leader", tone: "default" }))) return;
    try {
      await api.post(`/teams/${assignFor.id}/unassign-leader`, { user_id: leader.user_id });
      toast.success("Leader removed");
      await refreshAssign(assignFor.id);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openNew = () => { setEditing(null); setForm(empty); setMemberSearch(""); setOpen(true); };
  const openEdit = (t) => { setEditing(t.id); setForm({ ...empty, ...t }); setMemberSearch(""); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/teams/${editing}`, form);
      else await api.post("/teams", form);
      toast.success("Team saved"); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (id) => { const t = teams.find((x) => x.id === id); if (!(await confirm({ title: "Delete team?", description: `Delete "${t?.name || "this team"}" and all its tasks? This cannot be undone.`, confirmLabel: "Delete team" }))) return; await api.delete(`/teams/${id}`); logDeletion("team", t?.name || "Team"); toast.success("Deleted"); load(); };

  const toggleMember = (id) => {
    const s = new Set(form.member_ids);
    s.has(id) ? s.delete(id) : s.add(id);
    setForm({ ...form, member_ids: [...s] });
  };

  const openTasks = async (team) => {
    setTasksOpen(team);
    const { data } = await api.get("/tasks", { params: { team_id: team.id } });
    setTasks(data);
  };
  const addTask = async (e) => {
    e.preventDefault();
    await api.post("/tasks", { ...taskForm, team_id: tasksOpen.id });
    setTaskForm({ title: "", assignee_name: "", priority: "medium", status: "todo", due_date: "" });
    const { data } = await api.get("/tasks", { params: { team_id: tasksOpen.id } });
    setTasks(data); load();
  };
  const cycleStatus = async (t) => {
    const next = t.status === "todo" ? "in_progress" : t.status === "in_progress" ? "done" : "todo";
    await api.put(`/tasks/${t.id}`, { ...t, status: next });
    const { data } = await api.get("/tasks", { params: { team_id: tasksOpen.id } });
    setTasks(data); load();
  };
  const delTask = (id) => { const t = tasks.find((x) => x.id === id); setTasks(tasks.filter((x) => x.id !== id)); undoableDelete({ label: t?.title || "Task", entityType: "task", perform: () => api.delete(`/tasks/${id}`), onUndo: () => { setTasks((prev) => (t && !prev.some((x) => x.id === t.id) ? [...prev, t] : prev)); load(); } }); };

  const STATUS_STYLE = { todo: { bg: "#56626A1A", c: "#56626A", l: "To Do" }, in_progress: { bg: "#0844381A", c: "#084438", l: "In Progress" }, done: { bg: "#17A88A1A", c: "#17A88A", l: "Done" } };
  const PRIO = { high: "#0B5E4E", medium: "#084438", low: "#A8A29E" };
  const teamRows = [...teams].filter((t) => (t.name || "").toLowerCase().includes(teamSearch.trim().toLowerCase())).sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { sensitivity: "base" }));
  const teamsPaged = usePaged(teamRows, 25);

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="sj-page-hero flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Teams</h1>
          <p style={{ color: "var(--sj-text-2)" }}>Ministries, departments & their tasks</p>
        </div>
        <button data-testid="add-team-button" onClick={openNew} className="flex items-center gap-2 rounded-full px-5 py-2.5 text-white font-medium shadow-sm hover:brightness-105 transition-all" style={{ background: "var(--sj-primary)" }}>
          <Plus className="h-5 w-5" /> New Team
        </button>
      </div>

      <TeamsOverview />

      {applications.length > 0 && (
        <div className={`${CARD} p-6`} data-testid="applications-panel">
          <div className="flex items-center gap-2 mb-4">
            <UserCheck className="h-5 w-5" style={{ color: "var(--sj-primary)" }} />
            <h3 className="font-head text-xl font-semibold">Pending Applications to Serve</h3>
            <span className="rounded-full px-2.5 py-0.5 text-xs font-bold text-white" style={{ background: "var(--sj-primary)" }}>{applications.length}</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {applications.map((a) => (
              <div key={a.id} data-testid={`application-${a.id}`} className="rounded-xl border p-4 flex items-start justify-between gap-3" style={{ borderColor: "var(--sj-border)" }}>
                <div className="min-w-0">
                  <div className="font-semibold text-sm">{a.member_name}</div>
                  <div className="text-xs" style={{ color: "var(--sj-text-2)" }}>wants to join <strong>{a.team_name}</strong></div>
                  {a.reason && <p className="text-xs mt-1 italic" style={{ color: "var(--sj-text-3)" }}>"{a.reason}"</p>}
                </div>
                <div className="flex gap-2 shrink-0">
                  <button data-testid={`approve-${a.id}`} onClick={() => decideApp(a.id, "approve")} className="h-8 w-8 rounded-full flex items-center justify-center text-white hover:brightness-105" style={{ background: "var(--sj-secondary)" }} title="Approve"><Check className="h-4 w-4" /></button>
                  <button data-testid={`reject-${a.id}`} onClick={() => decideApp(a.id, "reject")} className="h-8 w-8 rounded-full flex items-center justify-center border hover:bg-red-50 transition-colors" style={{ borderColor: "#DC6A5C", color: "#C0392B" }} title="Reject"><X className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}


      <div className="relative max-w-sm" data-testid="teams-search-wrap">
        <Search className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: "var(--sj-primary)" }} />
        <input data-testid="teams-search" value={teamSearch} onChange={(e) => setTeamSearch(e.target.value)}
          placeholder="Search teams by name…"
          className="w-full bg-white border-2 border-[#0B5E4E] rounded-xl outline-none focus:ring-2 focus:ring-[#0B5E4E]/30 shadow-sm pl-10 pr-4 py-2.5 text-sm" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="teams-grid">
        {teamsPaged.pageItems.map((t) => (
          <div key={t.id} data-testid={`team-card-${t.id}`} className={`${CARD} p-6 transition-all hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(45,45,45,0.08)]`}>
            <div className="flex items-start justify-between">
              <div className="h-11 w-11 rounded-xl flex items-center justify-center" style={{ background: "#17A88A1A" }}><Users2 className="h-6 w-6" style={{ color: "#17A88A" }} /></div>
              <div className="flex gap-1">
                <button data-testid={`edit-team-${t.id}`} onClick={() => openEdit(t)} className="p-2 rounded-lg hover:bg-[#E3F1EC]" style={{ color: "var(--sj-secondary)" }}><Pencil className="h-4 w-4" /></button>
                <button data-testid={`delete-team-${t.id}`} onClick={() => del(t.id)} className="p-2 rounded-lg hover:bg-[#E3F1EC]" style={{ color: "var(--sj-primary)" }}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
            <span className="mt-4 inline-flex items-center gap-1.5 flex-wrap">
              <span className="inline-block text-xs font-semibold uppercase tracking-wider px-2.5 py-0.5 rounded-full" style={{ background: "var(--sj-peach)", color: "var(--sj-primary)" }}>{t.category}</span>
              {t.confidential && (
                <span data-testid={`team-confidential-badge-${t.id}`} title="Confidential — hidden from members; they can't see or apply to this team"
                  className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-0.5 rounded-full" style={{ background: "#FBEEE6", color: "#B45309" }}>
                  <Lock className="h-3 w-3" /> Confidential
                </span>
              )}
            </span>
            <h3 className="mt-2 font-head text-lg font-semibold">{t.name}</h3>
            <p className="text-sm line-clamp-2 mt-1" style={{ color: "var(--sj-text-2)" }}>{t.description}</p>
            {(() => {
              const heads = (t.leaders || []).map((l) => l.name).filter(Boolean);
              const nameById = Object.fromEntries(members.map((m) => [m.id, `${m.first_name} ${m.last_name}`]));
              const assistants = (t.assistant_head_ids || []).map((id) => nameById[id]).filter(Boolean);
              if (heads.length === 0 && assistants.length === 0) {
                return <button data-testid={`assign-leader-${t.id}`} onClick={() => openAssign(t)} className="mt-3 flex items-center gap-1.5 text-sm font-medium" style={{ color: "var(--sj-primary)" }}><Crown className="h-4 w-4" /> Assign team leaders</button>;
              }
              return (
                <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid={`team-leaders-${t.id}`}>
                  {heads.map((n, i) => (
                    <span key={`h${i}`} className="inline-flex items-center gap-1 text-xs font-medium rounded-full pl-1.5 pr-2.5 py-0.5" style={{ background: "#FBEAD7", color: "#8a5a1a" }} data-testid={`role-badge-head-${t.id}-${i}`}>
                      <Crown className="h-3.5 w-3.5" /> {n} <span className="text-[9px] uppercase font-bold opacity-80">Head</span>
                    </span>
                  ))}
                  {assistants.map((n, i) => (
                    <span key={`a${i}`} className="inline-flex items-center gap-1 text-xs font-medium rounded-full pl-1.5 pr-2.5 py-0.5" style={{ background: "#E3F1EC", color: "var(--sj-primary)" }} data-testid={`role-badge-assistant-${t.id}-${i}`}>
                      <UserCheck className="h-3.5 w-3.5" /> {n} <span className="text-[9px] uppercase font-bold opacity-80">Assistant</span>
                    </span>
                  ))}
                  <button data-testid={`assign-leader-${t.id}`} onClick={() => openAssign(t)} className="text-xs underline ml-1" style={{ color: "var(--sj-primary)" }}>manage</button>
                </div>
              );
            })()}
            <div className="mt-4 flex items-center justify-between text-sm" style={{ color: "var(--sj-text-2)" }}>
              <span>{t.member_count} members</span>
              <span>{t.open_tasks} open tasks</span>
            </div>
            <div className="mt-3 flex items-center justify-between rounded-xl px-3 py-2 border" style={{ borderColor: "var(--sj-border)", background: t.can_call ? "#E3F1EC" : "#F7F7F5" }} data-testid={`team-calling-row-${t.id}`}>
              <div className="flex items-center gap-1.5 text-sm font-medium" style={{ color: t.can_call ? "var(--sj-primary)" : "var(--sj-text-2)" }}>
                <PhoneCall className="h-4 w-4" /> Can call members
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={!!t.can_call}
                data-testid={`team-calling-toggle-${t.id}`}
                onClick={() => toggleCalling(t)}
                className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
                style={{ background: t.can_call ? "var(--sj-primary)" : "#C7CDD1" }}
              >
                <span className="inline-block h-5 w-5 transform rounded-full bg-white transition-transform" style={{ transform: t.can_call ? "translateX(22px)" : "translateX(2px)" }} />
              </button>
            </div>
            <div className="mt-2 flex items-center justify-between rounded-xl px-3 py-2 border" style={{ borderColor: "var(--sj-border)", background: t.confidential ? "#FBEEE6" : "#F7F7F5" }} data-testid={`team-confidential-row-${t.id}`}>
              <div className="flex items-center gap-1.5 text-sm font-medium" style={{ color: t.confidential ? "#B45309" : "var(--sj-text-2)" }}>
                {t.confidential ? <Lock className="h-4 w-4" /> : <LockOpen className="h-4 w-4" />}
                {t.confidential ? "Confidential — members can't apply" : "Members can apply to join"}
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={!!t.confidential}
                data-testid={`team-confidential-toggle-${t.id}`}
                onClick={() => toggleConfidential(t)}
                title={t.confidential ? "Make this team open for members to apply" : "Make this team confidential (hidden from members)"}
                className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors"
                style={{ background: t.confidential ? "#D97706" : "#C7CDD1" }}
              >
                <span className="inline-block h-5 w-5 transform rounded-full bg-white transition-transform" style={{ transform: t.confidential ? "translateX(22px)" : "translateX(2px)" }} />
              </button>
            </div>
            {t.confidential && <QuickAddMember team={t} members={members} onAdd={addMemberToTeam} />}
            <button data-testid={`team-tasks-${t.id}`} onClick={() => openTasks(t)} className="mt-4 w-full flex items-center justify-center gap-2 rounded-full py-2.5 font-medium text-white hover:brightness-105 transition-all" style={{ background: "var(--sj-secondary)" }}>
              <ListTodo className="h-4 w-4" /> Manage Tasks
            </button>
          </div>
        ))}
      </div>
      <Paginator page={teamsPaged.page} pageCount={teamsPaged.pageCount} onPage={teamsPaged.setPage} testid="teams-paginator" />

      {/* Team form */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head text-2xl">{editing ? "Edit Team" : "New Team"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2">
            <input data-testid="team-name-input" required placeholder="Team name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
            <div className="grid grid-cols-2 gap-4">
              <input data-testid="team-lead-input" placeholder="Team lead" value={form.lead_name} onChange={(e) => setForm({ ...form, lead_name: e.target.value })} className={inputCls} />
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls}>
                <option>Leadership</option><option>Ministry</option><option>Care</option><option>Admin</option><option>Outreach</option>
              </select>
            </div>
            <textarea placeholder="Description" rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} />
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: "var(--sj-text-3)" }}>Members ({form.member_ids.length})</div>
              {form.member_ids.length > 0 && (
                <div className="rounded-xl border p-2.5 mb-2" style={{ borderColor: "var(--sj-border)", background: "var(--sj-peach, #E3F1EC)" }} data-testid="team-current-members">
                  <div className="text-[11px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-primary)" }}>Currently in this team</div>
                  <div className="flex flex-wrap gap-1.5">
                    {form.member_ids.map((id) => {
                      const m = members.find((x) => x.id === id);
                      const label = m ? `${m.first_name} ${m.last_name}` : "Unknown member";
                      return (
                        <span key={id} data-testid={`team-current-member-${id}`} className="inline-flex items-center gap-1 text-xs font-medium bg-white rounded-full pl-2.5 pr-1 py-0.5 border" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text)" }}>
                          {label}
                          <button type="button" data-testid={`team-remove-member-${id}`} onClick={() => toggleMember(id)} title="Remove from team" className="h-4 w-4 rounded-full flex items-center justify-center hover:bg-[#E3F1EC] transition-colors" style={{ color: "var(--sj-primary)" }}>
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: "var(--sj-text-3)" }} />
                <input data-testid="team-member-search" placeholder="Search members to add..." value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)} className={`${inputCls} pl-9`} />
              </div>
              <div className="rounded-xl border p-2 max-h-40 overflow-y-auto space-y-1" style={{ borderColor: "var(--sj-border)" }}>
                {(() => {
                  const q = memberSearch.trim().toLowerCase();
                  const list = q ? members.filter((m) => `${m.first_name} ${m.last_name}`.toLowerCase().includes(q)) : members;
                  if (list.length === 0) return <div className="text-sm text-center py-3" style={{ color: "var(--sj-text-3)" }}>No members match "{memberSearch}"</div>;
                  return list.map((m) => (
                    <label key={m.id} data-testid={`team-member-option-${m.id}`} className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-[#EDF2F4] cursor-pointer text-sm">
                      <input type="checkbox" checked={form.member_ids.includes(m.id)} onChange={() => toggleMember(m.id)} className="accent-[#17A88A]" />
                      {m.first_name} {m.last_name}
                    </label>
                  ));
                })()}
              </div>
            </div>
            <DialogFooter>
              <button type="button" onClick={() => setOpen(false)} className="rounded-full px-5 py-2.5 border font-medium" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>Cancel</button>
              <button type="submit" data-testid="save-team-button" className="rounded-full px-6 py-2.5 text-white font-semibold hover:brightness-105 transition-all" style={{ background: "var(--sj-primary)" }}>Save</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Tasks dialog */}
      <Dialog open={!!tasksOpen} onOpenChange={(o) => !o && setTasksOpen(null)}>
        <DialogContent className="max-w-xl max-h-[90vh] flex flex-col">
          <DialogHeader><DialogTitle className="font-head text-2xl">{tasksOpen?.name} · Tasks</DialogTitle></DialogHeader>
          <form onSubmit={addTask} className="flex flex-wrap gap-2 py-2 items-center">
            <input data-testid="task-title-input" required placeholder="New task..." value={taskForm.title} onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })} className={`${inputCls} flex-1 min-w-[160px]`} />
            <input placeholder="Assignee" value={taskForm.assignee_name} onChange={(e) => setTaskForm({ ...taskForm, assignee_name: e.target.value })} className={`${inputCls} w-32`} />
            <input type="date" value={taskForm.due_date} onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })} className={`${inputCls} w-40`} />
            <select value={taskForm.priority} onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })} className={`${inputCls} w-28`}><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option></select>
            <button type="submit" data-testid="add-task-button" className="rounded-full px-4 py-2.5 text-white font-medium hover:brightness-105" style={{ background: "var(--sj-primary)" }}><Plus className="h-4 w-4" /></button>
          </form>
          <div className="flex-1 overflow-y-auto space-y-2" style={{ maxHeight: 380 }} data-testid="task-list">
            {tasks.length === 0 && <div className="text-center py-8 text-sm" style={{ color: "var(--sj-text-3)" }}>No tasks yet.</div>}
            {tasks.map((t) => {
              const s = STATUS_STYLE[t.status];
              return (
                <div key={t.id} className="flex items-center gap-3 p-3 rounded-xl border" style={{ borderColor: "var(--sj-border)" }}>
                  <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: PRIO[t.priority] }} title={t.priority} />
                  <div className="flex-1 min-w-0">
                    <div className={`font-medium text-sm ${t.status === "done" ? "line-through opacity-60" : ""}`}>{t.title}</div>
                    <div className="text-xs" style={{ color: "var(--sj-text-3)" }}>{t.assignee_name || "Unassigned"}{t.due_date ? ` · due ${ukDate(t.due_date)}` : ""}</div>
                  </div>
                  <button data-testid={`task-status-${t.id}`} onClick={() => cycleStatus(t)} className="rounded-full px-3 py-1 text-xs font-semibold" style={{ background: s.bg, color: s.c }}>{s.l}</button>
                  <button onClick={() => delTask(t.id)} className="p-1.5 rounded-lg hover:bg-[#E3F1EC]" style={{ color: "var(--sj-primary)" }}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign leader dialog */}
      <Dialog open={!!assignFor} onOpenChange={(o) => !o && setAssignFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-head text-2xl">Team leaders · {assignFor?.name}</DialogTitle>
          <DialogDescription>A team can have several leaders. Each gets a scoped login to manage this team and approve serving applications.</DialogDescription>
          </DialogHeader>
          {assignFor?.leaders && assignFor.leaders.length > 0 && (
            <div className="mt-2 space-y-1.5" data-testid="current-leaders-list">
              <label className="block text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--sj-text-3)" }}>Current leaders</label>
              {assignFor.leaders.map((l) => (
                <div key={l.user_id} data-testid={`current-leader-${l.user_id}`} className="flex items-center justify-between rounded-xl px-3 py-2 border" style={{ borderColor: "var(--sj-border)" }}>
                  <div className="flex items-center gap-2 min-w-0">
                    <Crown className="h-4 w-4 shrink-0" style={{ color: "var(--sj-ochre)" }} />
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{l.name || "Leader"}</div>
                      {l.email && <div className="text-xs truncate" style={{ color: "var(--sj-text-3)" }}>{l.email}</div>}
                    </div>
                  </div>
                  <button type="button" data-testid={`remove-leader-${l.user_id}`} onClick={() => unassignLeader(l)} className="text-xs font-semibold px-2.5 py-1 rounded-full border hover:bg-red-50 shrink-0" style={{ borderColor: "#DC6A5C", color: "#C0392B" }}>Remove</button>
                </div>
              ))}
            </div>
          )}
          {assignFor && (() => {
            const tm = members.filter((m) => (assignFor.member_ids || []).includes(m.id));
            const ah = assignFor.assistant_head_ids || [];
            const leaderMemberIds = new Set();
            return (
              <div className="mt-3 space-y-1.5" data-testid="assistant-heads-section">
                <label className="block text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--sj-text-3)" }}>Assistant Heads of Department</label>
                {tm.length === 0 ? (
                  <p className="text-xs italic" style={{ color: "var(--sj-text-3)" }}>Add members to this team first.</p>
                ) : (
                  <div className="max-h-40 overflow-y-auto space-y-1 pr-1">
                    {tm.map((m) => {
                      const on = ah.includes(m.id);
                      return (
                        <label key={m.id} data-testid={`assistant-head-${m.id}`} className="flex items-center gap-2.5 rounded-xl px-3 py-2 border cursor-pointer hover:bg-[#EDF2F4]" style={{ borderColor: "var(--sj-border)" }}>
                          <input type="checkbox" checked={on} onChange={() => toggleAssistantHead(assignFor, m.id)} className="h-4 w-4 accent-[#0B5E4E]" />
                          <span className="text-sm font-medium">{m.first_name} {m.last_name}</span>
                          {on && <span className="ml-auto text-[10px] uppercase font-bold rounded px-1.5 py-0.5" style={{ background: "var(--sj-peach)", color: "var(--sj-primary)" }}>Assistant Head</span>}
                        </label>
                      );
                    })}
                  </div>
                )}
                <p className="text-[11px]" style={{ color: "var(--sj-text-3)" }}>Everyone else on the team is a Team Member. Team leaders above are the Heads of Department.</p>
              </div>
            );
          })()}
          <form onSubmit={saveAssign} className="space-y-4 mt-3">
            <label className="block text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--sj-text-3)" }}>Add a leader</label>
            <div className="grid grid-cols-2 gap-2 p-1 rounded-xl" style={{ background: "#EDF2F4" }}>
              <button type="button" data-testid="assign-mode-member" onClick={() => setAssignMode("member")} className="rounded-lg py-2 text-sm font-medium transition-colors" style={{ background: assignMode === "member" ? "var(--sj-primary)" : "transparent", color: assignMode === "member" ? "#fff" : "var(--sj-text-2)" }}>
                Choose a team member
              </button>
              <button type="button" data-testid="assign-mode-email" onClick={() => setAssignMode("email")} className="rounded-lg py-2 text-sm font-medium transition-colors" style={{ background: assignMode === "email" ? "var(--sj-primary)" : "transparent", color: assignMode === "email" ? "#fff" : "var(--sj-text-2)" }}>
                Set up with email
              </button>
            </div>

            {assignMode === "member" ? (
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>Promote an existing team member</label>
                {(() => {
                  const tm = assignFor ? members.filter((m) => (assignFor.member_ids || []).includes(m.id)) : [];
                  if (tm.length === 0) return <p className="text-sm italic" style={{ color: "var(--sj-text-3)" }}>No members on this team yet. Add members to the team first, or use "Set up with email".</p>;
                  return (
                    <select data-testid="assign-member-select" value={assignForm.member_id} onChange={(e) => setAssignForm({ ...assignForm, member_id: e.target.value })} className={`${inputCls} w-full`}>
                      <option value="">Select a member…</option>
                      {tm.map((m) => <option key={m.id} value={m.id}>{m.first_name} {m.last_name}</option>)}
                    </select>
                  );
                })()}
                <p className="text-xs mt-2" style={{ color: "var(--sj-text-3)" }}>No email needed — they'll lead this team using their existing login.</p>
              </div>
            ) : (
              <>
                <p className="text-sm" style={{ color: "var(--sj-text-2)" }}>Use an existing member's email to promote them, or set a password to create a new leader login.</p>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>Leader name</label>
                  <input data-testid="assign-name-input" value={assignForm.name} onChange={(e) => setAssignForm({ ...assignForm, name: e.target.value })} className={`${inputCls} w-full`} />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>Email</label>
                  <input data-testid="assign-email-input" type="email" value={assignForm.email} onChange={(e) => setAssignForm({ ...assignForm, email: e.target.value })} className={`${inputCls} w-full`} />
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wider mb-1.5" style={{ color: "var(--sj-text-3)" }}>Password <span className="normal-case font-normal">(only for a new account, min 6 chars)</span></label>
                  <input data-testid="assign-password-input" type="password" value={assignForm.password} onChange={(e) => setAssignForm({ ...assignForm, password: e.target.value })} className={`${inputCls} w-full`} />
                </div>
              </>
            )}
            <DialogFooter>
              <button type="button" onClick={() => setAssignFor(null)} className="rounded-full px-5 py-2.5 border font-medium" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>Done</button>
              <button type="submit" data-testid="save-assign-button" className="rounded-full px-6 py-2.5 text-white font-semibold hover:brightness-105 transition-all" style={{ background: "var(--sj-primary)" }}>Add Leader</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

